import Vec2 from 'vec2';

import { NORMALIZE_ARROW, OFFSET_MEASUREMENT } from '~/core/constants';

/**
 * Creates a double-headed arrow shape from a line segment.
 * The arrow consists of:
 * - A main line segment (mainLineStart to mainLineEnd) that's optionally offset from the original segment
 * - Two arrow heads, one at each end of the line
 *
 * The arrow heads are created by:
 * 1. Rotating the direction vector by ±45° (π/4) for the start arrow
 * 2. Rotating the direction vector by ±135° (3π/4) for the end arrow
 * 3. Scaling each arrow head by NORMALIZE_ARROW length
 *
 * @param startPt - Starting point of the original line segment
 * @param endPt - Ending point of the original line segment
 * @param isInterior - If true, no offset is applied. If false, offset by OFFSET_MEASUREMENT
 *
 * @returns Array of Vec2 points in the following order:
 *   1. `mainLineStart`
 *   1. `mainLineEnd`
 *   1. `startTopArrow`
 *   1. `mainLineStart`
 *   1. `startBottomArrow`
 *   1. `endTopArrow`
 *   1. `mainLineEnd`
 *   1. `endBottomArrow`
 */
export const getArrowShapeFromSegment = (
  startPt: Vec2,
  endPt: Vec2,
  isInterior?: boolean
): Vec2[] => {
  // Calculate the normalized direction vector from start to end
  const directionVecX = endPt.x - startPt.x;
  const directionVecY = endPt.y - startPt.y;
  const directionLength = Math.sqrt(
    directionVecX * directionVecX + directionVecY * directionVecY
  );

  const directionVec = new Vec2(
    directionLength > 0 ? directionVecX / directionLength : 0,
    directionLength > 0 ? directionVecY / directionLength : 0
  );

  // Calculate the normal vector (perpendicular to direction)
  const normalVec = directionVec.rotate(Math.PI / 2, undefined, true);

  // Apply offset based on whether arrow is interior or exterior
  const offset = isInterior ? 0 : OFFSET_MEASUREMENT;

  // Create the main line segment points (mainLineStart and mainLineEnd)
  const mainLineStart = startPt.add(normalVec.multiply(offset, true), true);
  const mainLineEnd = endPt.add(normalVec.multiply(offset, true), true);

  // Initialize array with main line points
  const arrowPoints = [mainLineStart, mainLineEnd];

  // Create start arrow head (at mainLineStart)
  const startArrowTop = mainLineStart.add(
    directionVec.rotate(Math.PI / 4, undefined, true).multiply(NORMALIZE_ARROW),
    true
  );
  const startArrowBottom = mainLineStart.add(
    directionVec
      .rotate(-Math.PI / 4, undefined, true)
      .multiply(NORMALIZE_ARROW),
    true
  );

  arrowPoints.push(startArrowTop, mainLineStart, startArrowBottom);

  // Create end arrow head (at mainLineEnd)
  const endArrowTop = mainLineEnd.add(
    directionVec
      .rotate((Math.PI * 3) / 4, undefined, true)
      .multiply(NORMALIZE_ARROW),
    true
  );
  const endArrowBottom = mainLineEnd.add(
    directionVec
      .rotate((-Math.PI * 3) / 4, undefined, true)
      .multiply(NORMALIZE_ARROW),
    true
  );

  arrowPoints.push(endArrowTop, mainLineEnd, endArrowBottom);

  return arrowPoints;
};
